import { useState, useEffect, useMemo, type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import {
  updateTrailer,
  createTrailer
} from "../../../../../../store/admin/trailersStore";
import { useTrailersList } from "../../../../../../features/trailers/queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useInvalidateDomain } from "../../../../../../lib/invalidation";
import { getError } from "../../../../../../utils/getError";
import { useAlert } from "../../../../../../context/AlertContext";
import { useTranslation } from "react-i18next";
import { isFieldRequired } from "../../../../../../config/schemaConfig";
import type { Trailer, TrailerFormData, FilterParams, ViewType } from "../types";

export function useTrailerForm() {
  const { t } = useTranslation("adminSections");

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState<Trailer | null>(null);

  // Filter states
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedView, setSelectedView] = useState<ViewType>("list");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const debouncedSearch = useDebounce(searchQuery);

  // Form states
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [shipmentDetails, setShipmentDetails] = useState<string[]>([]);

  // Query & Alert
  const { showAlert } = useAlert();
  const invalidateDomain = useInvalidateDomain();

  const filterParams = useMemo<FilterParams>(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status_id:
        selectedFilter !== "all" && selectedFilter !== "" ? selectedFilter : undefined,
      type: selectedType !== "all" && selectedType !== "" ? selectedType : undefined,
      search: debouncedSearch || undefined,
    }),
    [currentPage, itemsPerPage, selectedFilter, selectedType, debouncedSearch],
  );

  const trailersQuery = useTrailersList(filterParams);
  const trailers = trailersQuery.data?.data ?? [];
  const meta = trailersQuery.data?.meta;
  const loading = trailersQuery.isLoading;
  const error = trailersQuery.error ? getError(trailersQuery.error) : null;

  const invalidateTrailers = () => invalidateDomain("trailers");

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<TrailerFormData>();

  // Watch form values for React Select
  const watchedValues = watch();

  // Validate Select fields based on the active schema for the given endpoint
  const validateSelectFields = (endpoint: "postRemolque" | "putRemolque"): boolean => {
    let isValid = true;
    const requireField = (field: string) => isFieldRequired("remolques", endpoint, field);

    const checks: Array<{ name: keyof TrailerFormData; field: string; message: string }> = [
      { name: "type", field: "type", message: t("trailers.validation.typeRequired") },
      { name: "status_id", field: "status_id", message: t("trailers.validation.statusRequired") },
      { name: "condition", field: "condition", message: t("trailers.validation.conditionRequired") },
      { name: "brakeType", field: "brakeType", message: t("trailers.validation.brakeTypeRequired") },
      { name: "suspensionType", field: "suspensionType", message: t("trailers.validation.suspensionTypeRequired") },
    ];

    for (const { name, field, message } of checks) {
      if (!requireField(field)) {
        clearErrors(name);
        continue;
      }
      if (!watchedValues[name]) {
        setError(name, { type: "required", message });
        isValid = false;
      } else {
        clearErrors(name);
      }
    }

    return isValid;
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter, selectedType, searchQuery]);

  // Handlers
  const handleEdit = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setValue("name", trailer.name);
    setValue("plate", trailer.plate);
    setValue("type", trailer.type);
    setValue("brand", trailer.brand);
    setValue("model", trailer.model);
    setValue("year", trailer.year);
    setValue("vin", trailer.vin);
    setValue("serialNumber", trailer.serialNumber);
    setValue("status_id", trailer.status_id || (trailer.status as any)?.id);
    setValue("condition", trailer.condition);
    setValue("description", trailer.description);
    setValue("capacity", trailer.capacity);
    setValue("maximumWeight", trailer.maximumWeight);
    setValue("axles", trailer.axles);
    setValue("length", trailer.length);
    setValue("width", trailer.width);
    setValue("height", trailer.height);
    setValue("brakeType", trailer.brakeType);
    setValue("suspensionType", trailer.suspensionType);
    setValue("registration", trailer.registration);
    setValue("insurance", trailer.insurance);
    setValue("permit", trailer.permit);
    setShipmentDetails(trailer.shipment_details || []);
    setImagePreview(trailer.image || null);
    setImageFile(null);
    setImageDeleted(false);
    setShowEditModal(true);
  };

  const handleDetails = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setShowDetailsModal(true);
  };

  const handleOpenCreateModal = () => {
    reset();
    setImagePreview(null);
    setImageFile(null);
    setImageDeleted(false);
    setShipmentDetails([]);
    setShowCreateModal(true);
  };

  const handleCloseModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowCreateModal(false);
    setSelectedTrailer(null);
    setImageFile(null);
    setImagePreview(null);
    setImageDeleted(false);
    setShipmentDetails([]);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageDeleted(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setImagePreview(null);
    setImageFile(null);
    setImageDeleted(true);
  };

  // Shipment Details handlers
  const handleAddShipmentDetail = () => {
    setShipmentDetails([...shipmentDetails, ""]);
  };

  const handleRemoveShipmentDetail = (index: number) => {
    setShipmentDetails(shipmentDetails.filter((_, i) => i !== index));
  };

  const handleShipmentDetailChange = (index: number, value: string) => {
    const newDetails = [...shipmentDetails];
    newDetails[index] = value;
    setShipmentDetails(newDetails);
  };

  const onSubmitEdit = async (data: TrailerFormData) => {
    if (!selectedTrailer) return;

    setLoadingSubmit(true);

    // Crear FormData
    const formData = new FormData();

    // Agregar la imagen si existe un nuevo archivo
    if (imageFile) {
      formData.append("imageFile", imageFile);
    } else if (imageDeleted) {
      // Si se eliminó la imagen, enviar null
      formData.append("image", "null");
    }

    // Agregar el resto de los campos, filtrando vacíos
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "" && key !== "image" && key !== "shipment_details") {
        formData.append(key, String(value));
      }
    });

    // Agregar shipment_details como JSON string para que el multipartParser lo parsee
    const filteredShipmentDetails = shipmentDetails.filter(detail => detail.trim() !== "");
    formData.append('shipment_details', JSON.stringify(filteredShipmentDetails));

    await updateTrailer(
      selectedTrailer.id,
      formData,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowEditModal(false);
        invalidateTrailers();
      },
      reset
    );
  };

  const onSubmitCreate = async (data: TrailerFormData) => {
    setLoadingSubmit(true);

    // Crear FormData
    const formData = new FormData();

    // Agregar la imagen si existe
    if (imageFile) {
      formData.append("imageFile", imageFile);
    }

    // Agregar campos adicionales
    formData.append("icon", "ri-truck-line");

    // Agregar el resto de los campos, filtrando vacíos
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "" && key !== "image" && key !== "shipment_details") {
        formData.append(key, String(value));
      }
    });

    // Agregar shipment_details como JSON string para que el multipartParser lo parsee
    const filteredShipmentDetails = shipmentDetails.filter(detail => detail.trim() !== "");
    formData.append('shipment_details', JSON.stringify(filteredShipmentDetails));

    await createTrailer(
      formData,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowCreateModal(false);
        invalidateTrailers();
      },
      reset
    );
  };

  // Wrapper functions that validate Select fields along with other fields
  const handleEditSubmit = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    const isSelectValid = validateSelectFields("putRemolque");
    const isFormValid = await trigger();
    if (!isSelectValid || !isFormValid) return;
    await handleSubmit(onSubmitEdit)(e);
  };

  const handleCreateSubmit = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    const isSelectValid = validateSelectFields("postRemolque");
    const isFormValid = await trigger();
    if (!isSelectValid || !isFormValid) return;
    await handleSubmit(onSubmitCreate)(e);
  };

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
  };

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    setCurrentPage(1);
  };

  const handleSearchChange = (search: string) => {
    setSearchQuery(search);
    setCurrentPage(1);
  };

  return {
    // Data
    trailers,
    meta,
    loading,
    error,
    invalidateTrailers,
    selectedTrailer,
    imagePreview,
    loadingSubmit,
    shipmentDetails,

    // Form
    register,
    errors,
    setValue,
    setError,
    clearErrors,
    reset,
    watchedValues,

    // Modal states
    showDetailsModal,
    showEditModal,
    showCreateModal,

    // Filter states
    selectedFilter,
    selectedType,
    searchQuery,
    selectedView,
    currentPage,
    itemsPerPage,

    // Handlers
    handleEdit,
    handleDetails,
    handleOpenCreateModal,
    handleCloseModals,
    handleImageChange,
    handleImageDelete,
    handleAddShipmentDetail,
    handleRemoveShipmentDetail,
    handleShipmentDetailChange,
    handleEditSubmit,
    handleCreateSubmit,
    handleFilterChange,
    handleTypeChange,
    handleSearchChange,
    setSelectedView,
    setCurrentPage,
  };
}
